---
label: Pairwise Dotplot
category: alignments
icon: GridIcon
keywords: [dotplot, dot plot, pairwise, comparison, similarity]
addedInVersion: "0.0.0"
---

# Pairwise Dotplot

A dotplot visualizes word matches between two aligned sequences. Each dot
represents a shared subsequence (word) found at corresponding positions in the
query and target. Diagonal lines indicate regions of similarity, while scattered
dots suggest noise or repetitive elements.

The dotplot tab is available only for pairwise alignments (exactly two
sequences). It appears automatically in the viewer header when you open a
qualifying alignment.

## How to Access

1. Open a pairwise alignment from the file tree.
2. Click the **Dotplot** tab in the viewer header.

## Toolbar Controls

### Word Size

The word size slider controls the minimum length of matching subsequences. A
larger word size filters out short, potentially spurious matches and highlights
stronger regions of similarity.

- **Nucleotide alignments:** adjustable from 7 to 20 (default 11).
- **Amino acid alignments:** adjustable from 2 to 6 (default 3).

### Strand Toggles

- **Forward** shows matches on the same strand. Forward matches appear in blue.
- **Reverse** shows reverse-complement matches. Reverse matches appear in red.

The reverse toggle is only available for nucleotide alignments. It is hidden
automatically for amino acid data.

### Zoom

Use the toolbar buttons to zoom in, zoom out, or reset the zoom level to fit
both sequences in the viewport.

## Keyboard Shortcuts

| Action | Shortcut |
|---|---|
| Scroll | <Kbd>←</Kbd> <Kbd>→</Kbd> <Kbd>↑</Kbd> <Kbd>↓</Kbd> |
| Jump to start | <Kbd>Home</Kbd> |
| Jump to end | <Kbd>End</Kbd> |
| Page scroll vertically | <Kbd>PageUp</Kbd> / <Kbd>PageDown</Kbd> |
| Zoom in | <Kbd>+</Kbd> or <Kbd>=</Kbd> |
| Zoom out | <Kbd>-</Kbd> |
| Reset zoom | <Kbd>0</Kbd> |
| Zoom with mouse | <KbdGroup><Mod /> + scroll wheel</KbdGroup> |

## Status Bar

The status bar at the bottom of the viewer shows:

- **Query length** and **target length** in nucleotides or residues.
- **Current zoom level** as a percentage.
- **Mouse position** coordinates when hovering over the plot.

## Tips

- Increase the word size to reduce noise on long sequences. Start with the
  default and adjust up if the plot looks cluttered.
- Strong diagonal lines indicate conserved regions. Broken diagonals suggest
  insertions or deletions.
- Parallel off-diagonal lines may indicate duplicated regions within one or both
  sequences.
